/* External */
import {
  memo, useEffect, useCallback, useState, useRef, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import CardCreate from '@dtvgo/card-create-profile';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButtonProfile from '../../../../components/FocusableButtonProfile';
import FocusableEditNameProfile from '../../../../components/FocusableEditNameProfile';
import FocusableEditAllowContentProfile from '../../../../components/FocusableEditAllowContentProfile';

/* Utils */
import { colors, TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { Routes, getLink } from '../../../../utils/router';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import {
  getInitials,
  formatName,
  featureFlagParentalControl,
  getAgeRating,
  checkUnratedContent,
  isProfileOptionsChanged,
} from '../../../../utils/profiles';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Assets */
import chevronLeft from '../../../../assets/svg/chevron-left.svg';

/* Storage */
import { editProfile } from '../../../../storage/profiles/actions';
import { editProfileAPI, getProfileAPI } from '../../../../services/profiles';
import { selectProfileName } from '../../../../storage/profiles/selectors';

/* Style */
import './CreateProfile.scss';
import { useLoading } from '../../../../providers/LoadingProvider';
import { preventDefaultDownArrowPress } from '../../../../utils/spatialNavigation';

function CreateProfile({ realFocusKey, setFocus, typeAction }) {
  const nameProfileFocusKey = `${realFocusKey}-name-profile`;
  const allowContentProfileFocusKey = `${realFocusKey}-allow-content-profile`;
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const userProfilesAgeRating = useSelector((state) => state.profile.saveProfileAgeRating);
  const userProfilesName = useSelector(selectProfileName);
  const [profiles] = useState(userProfiles);
  const [selectedProfile, setSelectedProfile] = useState({});
  const [profileName, setProfileName] = useState(location.state?.username);
  const [profileAgeRating, setProfileAgeRating] = useState('');
  const [profileIsUnratedContentChecked, setProfileIsUnratedContentChecked] = useState(true);
  const cardColor = params.id
    ? colors[location.state?.indexColor]
    : colors[(profiles?.length || 0) + 1];
  const indexColor = location.state?.indexColor;
  const buttonDelete = !selectedProfile?.isMainProfile && selectedProfile?.id;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const loading = useRef(true);
  const hasParentalControl = featureFlagParentalControl(country);
  const [loadingPage, setLoadingPage] = useState(true);
  const { hideLoading, showLoading } = useLoading();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [name, setName] = useState('');
  const [ageRating, setAgeRating] = useState('');
  const [isUnratedContentChecked, setIsUnratedContentChecked] = useState(true);
  const profileData = useMemo(() => (
    {
      name: (userProfilesName) || profileName,
      ageRating: (userProfilesAgeRating?.ageRating) ?? profileAgeRating,
      isUnratedContentChecked: checkUnratedContent(userProfilesAgeRating)
        ? userProfilesAgeRating?.isUnratedContentChecked
        : profileIsUnratedContentChecked,
    }
  ), [
    userProfilesName,
    profileName,
    userProfilesAgeRating,
    profileAgeRating,
    profileIsUnratedContentChecked,
  ]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      timeout = undefined;
      showLoading();
    }, 0);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      } else {
        hideLoading();
      }
    };
  }, [hideLoading, showLoading]);

  useEffect(() => {
    setTimeout(() => {
      hideLoading();
    }, 1000);
    clearTimeout();
  }, [hideLoading]);

  useEffect(() => {
    if (params.id) {
      setLoadingPage(true);
      getProfileAPI(params.id).then((res) => {
        setSelectedProfile(res.data);
        setProfileName(res.data.name);
        setProfileAgeRating(res.data.ageRating);
        setProfileIsUnratedContentChecked(res.data.isNotRatedContentAllowed);
        loading.current = !loading.current;
        setLoadingPage(false);
        return undefined;
      }).catch((error) => {
        setLoadingPage(false);
        history.push(
          getLink(Routes.Private.Profiles.Error),
          {
            create: false,
            source: 'MIDDLEWARE',
            location: 'PROFILE_EDIT',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.EDIT,
          },
        );
      });
    } else {
      setLoadingPage(false);
    }
  }, [
    history,
    params.id,
    profileName,
    profileData,
    hasParentalControl,
  ]);

  useEffect(() => {
    setName(profileData.name ? formatName(profileData.name) : profileName);
    setAgeRating(profileData.ageRating);
    setIsUnratedContentChecked(profileData.isUnratedContentChecked);
  }, [
    name,
    ageRating,
    isUnratedContentChecked,
    profileData,
    profileName,
    userProfilesAgeRating,
  ]);

  const gtmSubCategory = useMemo(() => {
    if (typeAction === TYPE_ACTIONS_PROFILE.EDIT) {
      return GTM_SUBCATEGORIES.PROFILE.PROFILES_EDIT;
    }
    return GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE;
  }, [typeAction]);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: gtmSubCategory,
        eventName,
      });
    }
  }, [commonTaggingEvent, gtmSubCategory]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  useEffect(() => {
    setFocus(nameProfileFocusKey);
  }, [nameProfileFocusKey, setFocus]);

  const handleEditProfileName = useCallback(({ id }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.EDIT_NAME_PROFILE });
    history.push(
      getLink(Routes.Private.Profiles.Edit.Name, { id }),
      {
        indexColor,
        typeCreateName: TYPE_ACTIONS_PROFILE.EDIT,
      },
    );
  }, [history, profileCommonTagEvent, indexColor]);

  const handleEditProfileAllowedContent = useCallback(({ id }) => {
    profileCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PROFILES_EDIT.CHANGE_ALLOWED_CONTENT_PROFILE,
    });
    history.push(
      getLink(Routes.Private.Profiles.Edit.AllowedContent, { id }),
      {
        indexColor,
        typeCreateName: TYPE_ACTIONS_PROFILE.EDIT,
      },
    );
  }, [history, indexColor, profileCommonTagEvent]);

  const handleDeleteProfile = useCallback(({ id }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.DELETE_CREATE_PROFILE });
    history.push(getLink(Routes.Private.Profiles.Delete, { id }), { indexColor });
  }, [history, profileCommonTagEvent, indexColor]);

  const clickConfirm = useCallback(() => {
    const changeParentalControl = isProfileOptionsChanged(
      getAgeRating(ageRating),
      isUnratedContentChecked,
      selectedProfile,
    );
    setButtonLoading(true);
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.CONFIRM_CREATE_PROFILE });

    if (name) {
      const editedData = {
        profile_id: selectedProfile?.id,
        profile_custom_age: getAgeRating(ageRating) || selectedProfile?.ageRating,
        profile_unrated_content: isUnratedContentChecked,
      };
      editProfileAPI(params.id, {
        name,
        ...(hasParentalControl && { ageRating: getAgeRating(ageRating) }),
        ...(hasParentalControl && { isNotRatedContentAllowed: isUnratedContentChecked }),
      }).then((res) => {
        dispatch(editProfile(res.data.profiles));
        localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
        history.push(getLink(Routes.Private.Profiles), {
          edit: true,
          changeParentalControl,
          ...(changeParentalControl && { profileEditedData: editedData }),
        });
        setButtonLoading(false);
        return true;
      }).catch((error) => {
        console.error(error);
        history.push(getLink(Routes.Private.Profiles.Error), {
          source: 'MIDDLEWARE',
          location: 'PROFILE_EDIT',
          type: 'UNEXPECTED',
          externCode: error?.response?.status,
          typeError: TYPE_ACTIONS_PROFILE.EDIT,
          parentalControl: hasParentalControl,
          changeParentalControl,
          ...(changeParentalControl && { profileEditedData: editedData }),
        });
        return false;
      });
    }
  }, [
    dispatch,
    history,
    params,
    profileCommonTagEvent,
    isUnratedContentChecked,
    ageRating,
    name,
    hasParentalControl,
    selectedProfile,
  ]);

  useOnBackPress({
    listener: () => {
      const eventName = (typeAction === TYPE_ACTIONS_PROFILE.EDIT)
        ? GTM_EVENT_NAMES.PROFILES_EDIT.BACK_PROFILES
        : GTM_EVENT_NAMES.PROFILES_CREATE.BACK_PROFILES_CREATE;
      profileCommonTagEvent({
        eventName,
      });
      history.goBack();
    },
  });

  if (loadingPage) {
    return (
      <div className="dtv-html5-c-user-profile-create">
        <div className="dtv-html5-c-user-profile--loading" />
      </div>
    );
  }

  // This regex returns the first 2 letters of the selected profile name
  const userNameSplit = getInitials(profileName);

  return (
    <div className="dtv-html5-c-user-profile-create">
      <div className="dtv-html5-c-user-profile-create--div">
        <div className="dtv-html5-c-title__back">
          <img
            src={getJsonImage('chevron-left') || chevronLeft}
            alt="Chevron Left"
            className="dtv-html5-c-title__back--chevronLeft"
          />
          {selectedProfile?.id
            ? (
              <h1><FormattedMessage id="profile.change" /></h1>
            ) : (
              <h1><FormattedMessage id="profile.create" /></h1>
            )}
        </div>
        <div className="dtv-html5-c-user-profile-create--row">
          <div className="dtv-html5-c-user-profile-create--row-col_profile">
            <div className={`${selectedProfile?.isMainProfile
              ? 'dtv-html5-c-user-profile-create--row-col_profile-divAdmin'
              : 'dtv-html5-c-user-profile-create--row-col_profile-div'}`}
            >
              <CardCreate
                color={cardColor}
                isInputNotEmpty={userNameSplit}
              />
              {selectedProfile?.isMainProfile
                && (
                  <p className="dtv-html5-c-user-profile-create--row-col_profile-pAdmin">
                    <FormattedMessage id="profile.main.title" />
                    <br />
                    <FormattedMessage id="profile.main.subtitle" />
                  </p>
                )}
            </div>
          </div>
          <div className="dtv-html5-c-user-profile-create--row-col_data">
            <div className="dtv-html5-c-user-profile-create--row-section">
              <div className="dtv-html5-c-user-profile-create--row-col_data_title">
                <h2 className="dtv-html5-c-user-profile-create--row-col_data_title_h2">
                  <FormattedMessage id="common.data" />
                </h2>
              </div>
              {selectedProfile?.id
                && (
                  <FocusableEditNameProfile
                    id={selectedProfile?.id}
                    profile={userProfilesName || profileName}
                    focusKey={nameProfileFocusKey}
                    indexColor={location.state?.indexColor}
                    onEnterPress={handleEditProfileName}
                    focused
                  />
                )}
              {hasParentalControl && (
                <>
                  <div className="dtv-html5-c-user-profile-create--row-col_data_title">
                    <h2 className="dtv-html5-c-user-profile-create--row-col_data_title_h2--marginTop">
                      <FormattedMessage id="profiles.parentalControl.title" />
                    </h2>
                  </div>
                  {selectedProfile?.id
                    && (
                      <FocusableEditAllowContentProfile
                        id={selectedProfile?.id}
                        focusKey={allowContentProfileFocusKey}
                        indexColor={location.state?.indexColor}
                        onEnterPress={(selectedProfile?.isMainProfile)
                          ? preventDefaultDownArrowPress
                          : handleEditProfileAllowedContent}
                        isMainProfile={selectedProfile?.isMainProfile}
                        userProfilesAgeRating={userProfilesAgeRating}
                        allowContentProfile={profileAgeRating}
                      />
                    )}
                    {selectedProfile?.isMainProfile && (
                      <div className="dtv-html5-c-user-profile-create--row-main_user_instruction">
                        <FormattedMessage id="profiles.parentalControl.admin.restriction" />
                      </div>
                    )}
                </>
              )}
            </div>
            <div className="dtv-html5-c-user-profile-create--button">
              {selectedProfile?.id
                && (
                  <FocusableButtonProfile
                    type="submit"
                    secondGray
                    bordered
                    focused
                    className="dtv-html5-c-user-profile-create--button-pattern"
                    onEnterPress={clickConfirm}
                    disabled={buttonLoading}
                  >
                    <FormattedMessage id="common.confirm" />
                  </FocusableButtonProfile>
                )}
              {buttonDelete
                && (
                  <FocusableButtonProfile
                    type="submit"
                    secondGray
                    bordered
                    focused
                    className="dtv-html5-c-user-profile-create--button-pattern"
                    id={selectedProfile?.id}
                    indexColor={location.state.indexColor}
                    onEnterPress={handleDeleteProfile}
                  >
                    <FormattedMessage id="profile.delete" />
                  </FocusableButtonProfile>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateProfile.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
  typeAction: PropTypes.string,
};

CreateProfile.defaultProps = {
  typeAction: '',
};

export default memo(withFocusable()(memo(CreateProfile)));
